import {
  Fragment,
  HTMLAttributes,
  useCallback,
  useEffect,
  useState
} from 'react'
import { chunk } from 'lodash'

import Button from '@/components/base/Button'
import Spinner from '@/components/base/Spinner'
import Typography from '@/components/base/Typography'
import HeroOnlyText from '@/components/sections/HeroOnlyText'
import PostGrid from '@/components/sections/PostGrid'
import Spacer from '@/components/sections/Spacer'
import { useStore } from '@/contexts/store'
import { getPromiseFromFetch } from '@/utils/request'
import { API } from '@/constants'

import { cn } from '@/lib/utils'
import { WPPageData } from '@/types'

export type BlogProps = HTMLAttributes<HTMLElement> & {
  pageData?: WPPageData
  per_page?: number
}

const Blog = ({ pageData, per_page = 8 }: BlogProps) => {
  const { strings } = useStore()
  const [hasMore, setHasMore] = useState(false)
  const [page, setPage] = useState(1)
  const [isLoading, setLoading] = useState(false)
  const [collection, setCollection] = useState<any[]>(pageData?.posts as any[])

  const handleClick = () => {
    if (!hasMore) return
    setPage(page + 1)
  }

  /*------------------------------
  Get Params
  ------------------------------*/
  const getParams = useCallback(
    (page: number) => {
      const params = {
        params: {
          per_page,
          page
        }
      } as any

      return params
    },
    [per_page]
  )

  const getPosts = useCallback(
    async (page: number) => {
      setLoading(true)

      const { data, headers } = await getPromiseFromFetch({
        method: 'get',
        url: `${API.BASE}/posts`,
        params: getParams(page)
      })

      if (!data || data.length === 0) return

      setCollection((prev) => [...prev, ...data])

      const totalPages = headers['x-wp-totalpages']

      setHasMore(page < parseInt(totalPages))

      setLoading(false)
    },
    [getParams]
  )

  useEffect(() => {
    if (page > 1) getPosts(page)
  }, [page])

  return (
    <>
      {pageData && <HeroOnlyText {...pageData.acf.hero} />}
      <Spacer variant="xxl" theme="transparent" />
      {Array.isArray(collection) ? (
        <>
          {chunk(
            collection.map((post, index) => ({
              order: `${index >= 9 ? '' : 0}${index + 1}`,
              ...post
            })),
            4
          ).map((posts, index) => (
            <Fragment key={index}>
              <PostGrid items={posts} stickyAnimation={index === 0} />
              <Spacer variant="xxl" theme="transparent" />
            </Fragment>
          ))}
          {hasMore ? (
            <div className="flex justify-center">
              <Button
                type="button"
                variant="light"
                onClick={handleClick}
                className={cn({ 'pointer-events-none': isLoading })}
              >
                {isLoading ? (
                  <Spinner theme="dark" variant="small" />
                ) : (
                  <span data-label={strings?.loadmore_label}>
                    <Typography variant="label">
                      {strings?.loadmore_label}
                    </Typography>
                  </span>
                )}
              </Button>
              <Spacer variant="xxl" theme="transparent" />
            </div>
          ) : null}
        </>
      ) : (
        <>
          <Typography
            variant="body"
            component="p"
            className="text-center text-dark-500"
          >
            {strings?.no_posts_text}
          </Typography>
          <Spacer variant="xxl" theme="transparent" />
        </>
      )}
    </>
  )
}

export default Blog
